import { useEffect, useState, type FormEvent, type JSX } from 'react';
import type { TakiConfig } from '@taki/runtime-config';
import { isAuthOnEmulator, prechaufferConnexionGoogle, signInWithEmail, signInWithGoogle } from '@taki/auth';
import { Button, Card, Input, repliDepuisConfig, useBrandingPort } from '@taki/ui';
import { connexionLocaleDe } from './connexionLocale.js';
import { GoogleMark } from './GoogleMark.js';
import { messageDeConnexion } from './messageDeConnexion.js';
import { TakiWordmark } from './TakiWordmark.js';

export interface LoginScreenProps {
  config: TakiConfig;
}

/**
 * Connexion affichee par l'auth guard quand aucun utilisateur n'est connecte.
 * Page bg-bg, carte centree (bg-surface, shadow-panel), CTA lime.
 *
 * DEUX CHEMINS, ET L'ORDRE N'EST PAS UN HASARD. Google d'abord, parce que c'est
 * celui que tout le monde prendra : rien a retenir, rien a transmettre, rien a
 * faire fuiter. L'adresse et le mot de passe restent dessous, pour qui n'a pas
 * de compte Google.
 *
 * ENTRER N'EST PAS AVOIR ACCES. Ni l'un ni l'autre de ces boutons n'accorde
 * quoi que ce soit : ils obtiennent un JETON. C'est la porte
 * (`rattacherCompte`, cote serveur) qui regarde ensuite si l'adresse est
 * invitee dans cette organisation, et `AccesRefuseScreen` qui le dit quand elle
 * ne l'est pas.
 *
 * En DEV LOCAL, un second chemin s'ajoute sous le formulaire : entrer avec le
 * compte de l'emulateur, en un clic et sans rien saisir. Il n'apparait que si
 * les deux verrous de `connexionLocale.ts` sont leves. C'est le filet pour les
 * deux cas ou l'entree automatique du guard ne joue pas : on vient de se
 * deconnecter volontairement, ou la config porte `auto: false`.
 */
export function LoginScreen({ config }: LoginScreenProps): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Gapi charge avant le clic : Safari bloque une fenetre Google ouverte trop
  // tard apres le geste (`prechaufferConnexionGoogle`, `@taki/auth`).
  useEffect(() => {
    prechaufferConnexionGoogle();
  }, []);

  // Le compte de l'emulateur, ou `null` hors dev local (double verrou).
  const compteLocal = connexionLocaleDe(config, isAuthOnEmulator());

  /* LE NOM ET LE LOGO DU CLIENT, AVANT TOUTE CONNEXION. L'hote observe le
     document de branding des le montage, sans attendre l'auth (la lecture est
     publique dans les rules), precisement pour que cet ecran-ci porte la marque
     du client. Sans port, c'est la config de deploiement, comme avant. */
  const courant = useBrandingPort()?.courant ?? repliDepuisConfig(config.branding);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signInWithEmail(email, password);
      // En cas de succes, `useUser()` bascule en etat connecte et le guard
      // remplace cet ecran par le shell automatiquement ; rien d'autre a faire.
    } catch (err) {
      setError(messageDeConnexion(err));
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Entrer avec Google. Meme suite que le formulaire : le guard bascule tout
   * seul des que `useUser()` voit l'utilisateur.
   *
   * Refermer la fenetre Google ne laisse RIEN a l'ecran : `messageDeConnexion`
   * rend `null` pour ce cas-la, parce que c'est un geste, pas un echec.
   */
  async function handleGoogle(): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(messageDeConnexion(err));
    } finally {
      setSubmitting(false);
    }
  }

  /** Meme chemin que le formulaire, avec les identifiants du jeu de demo. */
  async function handleCompteLocal(): Promise<void> {
    if (!compteLocal) return;
    setError(null);
    setSubmitting(true);
    try {
      await signInWithEmail(compteLocal.email, compteLocal.password);
    } catch (err) {
      setError(messageDeConnexion(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-full w-full items-center justify-center bg-bg p-6 text-ink-900">
      <Card className="w-full max-w-sm p-8 shadow-panel">
        <div className="mb-7 flex flex-col items-center gap-3 text-center">
          {courant.logo ? (
            <img
              src={courant.logo}
              alt={courant.name}
              className="h-9 w-auto max-w-full object-contain"
            />
          ) : (
            <span className="text-lg font-semibold text-ink-900">
              {courant.name}
            </span>
          )}
          <p className="text-sm text-ink-500">Connexion a votre espace</p>
        </div>

        {/* UN SEUL endroit pour l'echec, AU-DESSUS des deux chemins : l'erreur
            de Google et celle du mot de passe se lisent au meme endroit, et on
            ne la cherche pas sous un formulaire qu'on n'a pas rempli. */}
        {error ? (
          <p role="alert" className="mb-4 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <Button
          type="button"
          variant="secondary"
          size="lg"
          className="w-full gap-2.5"
          disabled={submitting}
          onClick={() => {
            void handleGoogle();
          }}
        >
          <GoogleMark height={17} />
          Continuer avec Google
        </Button>

        {/* Le separateur dit que ce sont DEUX chemins vers la meme porte, pas
            une etape puis une autre. */}
        <div className="my-6 flex items-center gap-3" aria-hidden="true">
          <span className="h-px flex-1 bg-border" />
          <span className="text-[0.6875rem] uppercase tracking-[0.06em] text-ink-400">ou</span>
          <span className="h-px flex-1 bg-border" />
        </div>

        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Email</span>
            <Input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="vous@exemple.com"
            />
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Mot de passe</span>
            <Input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="••••••••"
            />
          </label>

          <Button type="submit" size="lg" className="w-full" disabled={submitting}>
            {submitting ? 'Connexion…' : 'Se connecter'}
          </Button>
        </form>

        {compteLocal ? (
          <div className="mt-6 flex flex-col gap-3 border-t border-border pt-6">
            <p className="text-xs text-ink-500">
              Emulateur local : le mot de passe est celui du jeu de demo, rien a saisir.
            </p>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="w-full"
              disabled={submitting}
              onClick={() => {
                void handleCompteLocal();
              }}
            >
              Entrer avec le compte local ({compteLocal.email})
            </Button>
          </div>
        ) : null}

        <div className="mt-7 flex items-center justify-center gap-2 opacity-50">
          <span className="text-[0.625rem] tracking-[0.04em] text-ink-500">powered by</span>
          <TakiWordmark height={16} className="text-ink-500" />
        </div>
      </Card>
    </div>
  );
}
